import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

const genSession = () =>
  "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genSession);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi! I'm Ela from Express Logistics. Ask me about pricing, timelines, packing, or anything about your upcoming move.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Sorry — our assistant is offline. Please call +91 96182 22500.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3.5 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-105 hover:shadow-primary/40 transition-transform"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Chat with us</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-3rem))] flex flex-col rounded-2xl bg-background border border-border shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 bg-primary text-primary-foreground">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-foreground/15 grid place-items-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-base leading-tight">Ela · AI Assistant</div>
                <div className="text-[11px] opacity-80">Usually replies in seconds</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
              className="p-1.5 rounded-full hover:bg-primary-foreground/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto p-5 space-y-3 bg-muted/40"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-background border border-border rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl bg-background border border-border flex gap-1">
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:.15s]" />
                  <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce [animation-delay:.3s]" />
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-border bg-background">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Ask about pricing, packing, timelines…"
                className="flex-1 resize-none max-h-32 px-4 py-2.5 rounded-full bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={loading || !input.trim()}
                className="w-10 h-10 grid place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
